import { useCallback } from "react";
import { useParams } from "react-router";
import { useFormik } from "formik";
import _ from "lodash";

import { RotateLeftOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import Paper from "components/Paper";
import RichTextEditorField from "components/RichTextEditorField";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { VALIDATION_SCHEMA } from "entities/Memo/components/Sections/RisksAndMitigants/constants";
import { IMemoRisk, riskUpdate } from "entities/Memo/sdk";

export interface IRiskEditDialog {
  sectionTitle: string;
  open: boolean;
  onUpdate: () => void;
  memoSectionId: number;
  riskData: IMemoRisk | null;
  handleSetRiskData: ({ riskData }: { riskData: IMemoRisk }) => void;
  onClose: () => void;
}

const RiskEditDialog = ({
  open,
  memoSectionId,
  onUpdate,
  riskData,
  handleSetRiskData,
  onClose,
  sectionTitle,
}: IRiskEditDialog) => {
  const params = useParams<{ dealId: string }>();
  const dealId = Number(params.dealId);

  const { show: showConfirmationDialog } = useConfirmationDialog();

  const handleSubmit = useCallback(
    (values: { id: number; title: string; description: string }) => {
      riskUpdate({
        dealId,
        memoSectionId,
        title: values.title,
        description: values.description,
        riskId: values.id,
      })
        .then(() => {
          onUpdate();
          onClose();
          toast.successMessage("Your item has been saved successfully!");
        })
        .catch(handleInvalidRequest);
    },
    [dealId, memoSectionId, onUpdate, onClose]
  );

  const formik = useFormik({
    initialValues: _.isNil(riskData)
      ? {
          id: 0,
          title: "",
          description: "",
        }
      : {
          id: riskData.id,
          title: riskData.title,
          description: riskData.description,
        },
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const onResetToOriginalClick = useCallback(() => {
    showConfirmationDialog({
      onConfirm: () => {
        if (_.isNil(riskData?.template)) {
          toast.errorMessage("This risk doesn't have a template to reset to.");
          return;
        }

        const resetRisk = {
          ...riskData,
          title: riskData.template.title,
          description: riskData.template.description,
        };

        handleSetRiskData({
          riskData: resetRisk,
        });
        formik.resetForm({
          values: {
            id: resetRisk.id,
            title: resetRisk.title,
            description: resetRisk.description,
          },
        });
      },
      confirmButtonText: "Confirm",
      message:
        "Are you sure you want to reset the risk to the original template?",
    });
  }, [formik, riskData, showConfirmationDialog, handleSetRiskData]);

  return (
    <Dialog onClose={onClose} open={open} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ padding: 2 }}>
        <Paper
          sx={{
            padding: theme.spacing(1, 2),
            backgroundColor: colors.blue10,
            borderRadius: "4px",
          }}
        >
          <Text variant="labelSmall" color={colors.blue100} fontWeight={500}>
            {sectionTitle}
          </Text>
          <Text variant="h2">{riskData?.title || <Skeleton />}</Text>
        </Paper>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          overflow: "visible",
        }}
      >
        <TextField
          {...formik.getFieldProps("title")}
          label="Title displayed"
          defaultValue={formik.values.title}
          fullWidth
          error={formik.touched.title && !!formik.errors.title}
          helperText={
            formik.touched.title && formik.errors.title
              ? formik.errors.title
              : ""
          }
        />
        <RichTextEditorField
          defaultValue={formik.values.description}
          onChange={(value) => formik.setFieldValue("description", value)}
          sx={{ minHeight: "300px" }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          padding: 2,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Tooltip
          title={
            _.isNil(riskData?.template)
              ? "This risk doesn't have a template to reset to."
              : undefined
          }
        >
          <span>
            <Button
              variant="outlined"
              onClick={onResetToOriginalClick}
              disabled={_.isNil(riskData?.template)}
            >
              <Stack direction="row" gap={1} alignItems="center">
                <RotateLeftOutlinedIcon
                  sx={{ width: "24px", height: "24px" }}
                />
                <Text variant="text2" fontWeight={500}>
                  Reset text to the original template
                </Text>
              </Stack>
            </Button>
          </span>
        </Tooltip>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>

          <Button onClick={formik.submitForm}>Save changes</Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default RiskEditDialog;
